/**
 * AthleteWaiverStatusCard — coach-dossier waiver-status widget.
 *
 * Shows the per-athlete waiver state at a glance — signed/expires
 * date, signer name, and a colored urgency band:
 *   - current  → green ("Signed ✓ · expires Mar 1, 2027")
 *   - expiring → amber ("Expires in 14 days")
 *   - expired  → red ("Expired Mar 5, 2026 — re-sign required")
 *   - missing  → red ("Not signed")
 *
 * Sachin spec 2026-02-28 — surfaces the per-athlete waiver record we
 * started writing under Option A so coaches can see legal coverage
 * without opening Mongo.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, ShieldCheck, ShieldAlert, ShieldX } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return iso; }
}

const PALETTE = {
    current: {
        Icon: ShieldCheck,
        ring: "border-emerald-700/50",
        bg: "bg-emerald-900/10",
        accent: "text-emerald-300",
        chip: "bg-emerald-700/20 text-emerald-200",
        label: "Signed",
    },
    expiring: {
        Icon: ShieldAlert,
        ring: "border-amber-700/50",
        bg: "bg-amber-900/10",
        accent: "text-amber-300",
        chip: "bg-amber-700/30 text-amber-100",
        label: "Expiring soon",
    },
    expired: {
        Icon: ShieldX,
        ring: "border-red-700/60",
        bg: "bg-red-900/15",
        accent: "text-red-300",
        chip: "bg-red-700/40 text-red-100",
        label: "Expired",
    },
    missing: {
        Icon: ShieldX,
        ring: "border-red-700/60",
        bg: "bg-red-900/15",
        accent: "text-red-300",
        chip: "bg-red-700/40 text-red-100",
        label: "Not signed",
    },
};

export default function AthleteWaiverStatusCard({ athleteId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!athleteId) return;
        let cancel = false;
        (async () => {
            try {
                const r = await axios.get(
                    `${API}/coach/athletes/${athleteId}/waiver/status`,
                    { withCredentials: true },
                );
                if (!cancel) setData(r.data);
            } catch (_e) {
                if (!cancel) setData(null);
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => { cancel = true; };
    }, [athleteId]);

    if (loading) {
        return (
            <div className="border border-zinc-900 bg-zinc-950/70 rounded-md p-4 flex items-center gap-2 text-xs text-zinc-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Loading waiver status…
            </div>
        );
    }
    if (!data) return null;
    const p = PALETTE[data.status] || PALETTE.missing;

    return (
        <div
            data-testid={`waiver-status-${athleteId}`}
            className={`border ${p.ring} ${p.bg} rounded-md p-4`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                    <p.Icon className={`w-5 h-5 ${p.accent} flex-shrink-0 mt-0.5`} />
                    <div className="min-w-0">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">
                            Participant Agreement
                        </div>
                        <div className={`text-base font-semibold mt-0.5 ${p.accent}`}>
                            {data.status === "current" && `Signed · expires ${fmt(data.expires_at)}`}
                            {data.status === "expiring" && `Expires ${fmt(data.expires_at)} · ${data.days_until_expiry}d left`}
                            {data.status === "expired" && `Expired ${fmt(data.expires_at)} — re-sign required`}
                            {data.status === "missing" && "Not on file — no waiver signed yet"}
                        </div>
                        {data.signed_at && (
                            <div className="text-[11px] text-zinc-400 mt-1.5 flex flex-wrap gap-x-2">
                                <span>Signed {fmt(data.signed_at)}</span>
                                {data.signed_name && <span>· by {data.signed_name}</span>}
                                {data.relationship && data.relationship !== "self" && (
                                    <span>({data.relationship})</span>
                                )}
                                {data.version && (
                                    <span className={data.version !== data.current_version ? "text-amber-400" : ""}>
                                        · {data.version}{data.version !== data.current_version ? " (outdated)" : ""}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <span className={`inline-flex items-center text-[10px] tracking-[0.2em] uppercase font-semibold px-2 py-0.5 flex-shrink-0 ${p.chip}`}>
                    {p.label}
                </span>
            </div>
            {(data.status === "missing" || data.status === "expired") && (
                <div className="mt-3 pt-3 border-t border-zinc-800/60 text-[11px] text-zinc-400 leading-relaxed">
                    The parent will be prompted to sign on their next login. To sign on their
                    behalf right now (e.g., paper waiver brought in at the front desk), use the
                    member portal &rarr; Welcome &rarr; Sign waiver flow with the parent&apos;s account.
                </div>
            )}
        </div>
    );
}
